import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import clsx from 'clsx';
import { useId, useRef, useState } from 'react';
import { Check, ChevronDown } from 'react-feather';

import {
  ExpertRequestStage,
  ExpertRequestState,
  ExpertRequestTransitionAction,
} from '@/__generated__/graphql';
import Tooltip from '@/componentsv2/Tooltip';

import { effectiveStage } from '../../utils/effectiveStage';
import { buildStageRows } from '../../utils/stageTransitions';
import { ER_STAGE_DOT, ER_STAGE_LABEL, ER_STAGE_PILL } from '../erStageTokens';

export interface ErStageDropdownProps {
  stage?: ExpertRequestStage | null;
  state?: ExpertRequestState | string | null;
  /** Fired with the chosen transition; the parent opens its confirm modal. */
  onSelect: (action: ExpertRequestTransitionAction) => void;
  /** The ER's `permissions` (ListAllowedActions) — the per-transition verbs the viewer may perform. */
  allowedActions?: readonly (string | null)[] | null;
  /** Blocks opening the menu while a transition is in flight. */
  disabled?: boolean;
  className?: string;
}

const LOCK_TOOLTIP_PERM = "You don't have permission to update the stage.";

/**
 * ErStageDropdown — the single entry point for lifecycle transitions (ENG-5290
 * T2, redesigned in ENG-5314). Shows ALL four stages in order so the sequence is
 * learnable: the current stage is marked and inert, available transitions are
 * selectable (the Active row carries a submit/resume/reopen hint), and
 * unavailable stages are locked with a tooltip. Read-only viewers still get the
 * full (all-locked) list — it never collapses to a static badge.
 */
export function ErStageDropdown({
  stage,
  state,
  onSelect,
  allowedActions,
  disabled,
  className,
}: ErStageDropdownProps): JSX.Element {
  const resolved = effectiveStage(stage, state);
  const rows = buildStageRows(resolved, allowedActions);
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const triggerId = useId();
  const menuId = useId();

  return (
    <div className={className}>
      <button
        ref={anchorRef}
        id={triggerId}
        type="button"
        data-testid="er-stage-dropdown-trigger"
        onClick={() => setOpen(true)}
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={clsx(
          'inline-flex w-fit items-center gap-[6px] whitespace-nowrap rounded-lg px-[10px] py-[7px] text-14 font-medium',
          ER_STAGE_PILL[resolved],
          'disabled:cursor-not-allowed disabled:opacity-60'
        )}
      >
        <span
          className={clsx('h-[7px] w-[7px] shrink-0 rounded-full', ER_STAGE_DOT[resolved])}
          aria-hidden="true"
        />
        {ER_STAGE_LABEL[resolved]}
        <ChevronDown size={16} className="shrink-0" />
      </button>
      <Menu
        id={menuId}
        anchorEl={anchorRef.current}
        open={open}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-labelledby': triggerId }}
      >
        {rows.map((row) => {
          const isCurrent = row.status === 'current';
          const isLocked = row.status === 'locked-seq' || row.status === 'locked-perm';

          const item = (
            <MenuItem
              key={row.stage}
              data-testid={`er-stage-option-${row.stage}`}
              disabled={isCurrent || isLocked}
              onClick={
                row.action
                  ? () => {
                      setOpen(false);
                      onSelect(row.action as ExpertRequestTransitionAction);
                    }
                  : undefined
              }
              className="flex items-center gap-8"
              // Current is inert but must NOT read as disabled/whitened — keep it
              // full-opacity so only the trailing checkmark marks it (no fill).
              sx={isCurrent ? { '&.Mui-disabled': { opacity: 1 } } : undefined}
            >
              <span
                className={clsx('h-[7px] w-[7px] shrink-0 rounded-full', ER_STAGE_DOT[row.stage])}
                aria-hidden="true"
              />
              <span>
                {ER_STAGE_LABEL[row.stage]}
                {row.parenthetical ? (
                  <span className="ml-6 text-12 italic text-on-surface-secondary">
                    {row.parenthetical}
                  </span>
                ) : null}
              </span>
              {isCurrent ? (
                <span className="ml-auto flex items-center pl-8">
                  <Check size={16} className="text-on-surface-secondary" aria-hidden="true" />
                  <span className="sr-only">current</span>
                </span>
              ) : null}
            </MenuItem>
          );

          if (!isLocked) return item;

          const title =
            row.status === 'locked-perm'
              ? LOCK_TOOLTIP_PERM
              : `Not available from the ${ER_STAGE_LABEL[resolved]} stage.`;
          return (
            <Tooltip
              key={row.stage}
              title={title}
              placement="left"
              slotProps={{ tooltip: { sx: { whiteSpace: 'nowrap', maxWidth: 'none' } } }}
            >
              <span>{item}</span>
            </Tooltip>
          );
        })}
      </Menu>
    </div>
  );
}
